import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { useEffect } from "react";
import { Bold, Italic, List, ListOrdered, Quote, Code, Heading1, Heading2, Image as ImageIcon, Link as LinkIcon, Undo, Redo } from "lucide-react";

export default function RichTextEditor({ value, onChange, placeholder = "Scrie conținutul...", testid = "rich-editor", minHeight = 200 }) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ inline: false, allowBase64: false }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer" } }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => onChange?.(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "idg-rich-editor prose prose-invert max-w-none focus:outline-none px-4 py-3",
        "data-testid": `${testid}-area`,
        style: `min-height: ${minHeight}px;`,
      },
    },
  });

  // Update editor when external value changes (e.g., loading existing page)
  useEffect(() => {
    if (editor && value != null && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return <div className="tf-surface tf-clip-card-sm h-40 animate-pulse" />;

  const Btn = ({ onClick, active, children, title, testid: tid }) => (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`w-8 h-8 flex items-center justify-center rounded text-xs transition-colors ${active ? "bg-[var(--tf-primary)] text-black" : "text-zinc-300 hover:bg-white/10"}`}
      data-testid={tid}
    >
      {children}
    </button>
  );

  const addImage = () => {
    const url = window.prompt("URL imagine:");
    if (url) editor.chain().focus().setImage({ src: url }).run();
  };
  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href;
    const url = window.prompt("URL link:", previousUrl);
    if (url === null) return;
    if (url === "") { editor.chain().focus().extendMarkRange("link").unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <div className="idg-rich-editor-wrap tf-surface tf-clip-card-sm border border-white/10" data-testid={testid}>
      <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-white/5 flex-wrap">
        <Btn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")} title="Bold" testid="rt-bold"><Bold className="w-3.5 h-3.5" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")} title="Italic" testid="rt-italic"><Italic className="w-3.5 h-3.5" /></Btn>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })} title="H1" testid="rt-h1"><Heading1 className="w-3.5 h-3.5" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} title="H2" testid="rt-h2"><Heading2 className="w-3.5 h-3.5" /></Btn>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <Btn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")} title="Listă" testid="rt-ul"><List className="w-3.5 h-3.5" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")} title="Listă numerotată" testid="rt-ol"><ListOrdered className="w-3.5 h-3.5" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")} title="Citat" testid="rt-quote"><Quote className="w-3.5 h-3.5" /></Btn>
        <Btn onClick={() => editor.chain().focus().toggleCodeBlock().run()} active={editor.isActive("codeBlock")} title="Cod" testid="rt-code"><Code className="w-3.5 h-3.5" /></Btn>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <Btn onClick={setLink} active={editor.isActive("link")} title="Link" testid="rt-link"><LinkIcon className="w-3.5 h-3.5" /></Btn>
        <Btn onClick={addImage} title="Imagine" testid="rt-image"><ImageIcon className="w-3.5 h-3.5" /></Btn>
        <div className="w-px h-5 bg-white/10 mx-1" />
        <Btn onClick={() => editor.chain().focus().undo().run()} title="Undo" testid="rt-undo"><Undo className="w-3.5 h-3.5" /></Btn>
        <Btn onClick={() => editor.chain().focus().redo().run()} title="Redo" testid="rt-redo"><Redo className="w-3.5 h-3.5" /></Btn>
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
